import { graphql } from "@/gql"

// QUERIES =====================================================================

// Minimal lookup used to resolve an id-only link to its canonical
// /r/:recordType/:slug URL (see the "record-by-id" redirect route).
export const fetchRecordRouteById = graphql(`
  query fetchRecordRouteById($id: Int!) {
    record: records_by_pk(id: $id) {
      id
      type
      slug
    }
  }
`)

// Detailed fetch — adds contacts, tags, recent times, backlinks (records
// that link TO this one), and listings (lists this record appears in).
// Use when a caller (e.g. AI context, document, export) needs the fuller
// picture of how the record is connected.
export const fetchDetailedRecordBySlug = graphql(`
  query fetchDetailedRecordBySlug($slug: String!, $type: String!) {
    records(where: { slug: { _eq: $slug }, type: { _eq: $type } }, limit: 1) {
      ...RecordFields
      owner: owner_person {
        id
        name
      }
      links {
        ...RecordLinkFields
      }
      sources {
        ...RecordSourceFields
      }
      backlinks {
        id
        schema_key
        record {
          ...RequiredRecordFields
          description
          data
          schema_id
          sources {
            ...RecordSourceFields
          }
        }
      }
      listings {
        ...RecordListingFields
        list {
          ...RequiredRecordFields
          parent {
            ...RequiredRecordFields
          }
        }
      }
      contacts {
        ...RecordContactFields
      }
      taggings {
        ...RecordTaggingFields
      }
      times(order_by: { start_at: desc }, limit: 10) {
        ...RecordTimeFields
      }
    }
  }
`)

export const fetchDetailedRecordById = graphql(`
  query fetchDetailedRecordById($id: Int!) {
    record: records_by_pk(id: $id) {
      ...RecordFields
      owner: owner_person {
        id
        name
      }
      links {
        ...RecordLinkFields
      }
      sources {
        ...RecordSourceFields
      }
      backlinks {
        id
        schema_key
        record {
          ...RequiredRecordFields
          description
          data
          schema_id
          sources {
            ...RecordSourceFields
          }
        }
      }
      listings {
        ...RecordListingFields
        list {
          ...RequiredRecordFields
          parent {
            ...RequiredRecordFields
          }
        }
      }
      contacts {
        ...RecordContactFields
      }
      taggings {
        ...RecordTaggingFields
      }
      times(order_by: { start_at: desc }, limit: 10) {
        ...RecordTimeFields
      }
    }
  }
`)

export const fetchRecordForMerge = graphql(`
  query fetchRecordForMerge($id: Int!) {
    record: records_by_pk(id: $id) {
      ...RecordFields
      links {
        ...RecordLinkFields
      }
      sources {
        ...RecordSourceFields
      }
      listings {
        ...RecordListingFields
        list {
          ...RecordFields
        }
      }
      contacts {
        ...RecordContactFields
      }
      attachments {
        ...RecordAttachmentFields
      }
      taggings {
        ...RecordTaggingFields
      }
      children {
        ...RecordFields
      }
    }
  }
`)

export const checkRecordExists = graphql(`
  query checkRecordExists($slug: String!, $type: String!) {
    records(where: { slug: { _eq: $slug }, type: { _eq: $type } }, limit: 1) {
      id
    }
  }
`)

// Direct children of a record, one level deep. Used by the record-tree block
// to lazily expand a node. The page record only preloads its immediate
// children, so deeper levels are fetched on demand as nodes expand. Archived
// children are left out.
export const fetchChildRecords = graphql(`
  query fetchChildRecords($parent_id: Int!, $orderBy: [records_order_by!] = { created_at: asc }) {
    records(
      where: { parent_id: { _eq: $parent_id }, archived_at: { _is_null: true } }
      order_by: $orderBy
    ) {
      ...RecordFields
    }
  }
`)

// A record's children of one type, with the owned rows a grid of them shows:
// event times, contacts (location, website) and taggings. Powers useChildren.
// Archived children are included; the collection hides them until asked.
export const fetchChildren = graphql(`
  query fetchChildren($parentId: Int!, $type: String!) {
    records(
      where: { parent_id: { _eq: $parentId }, type: { _eq: $type } }
      order_by: { name: asc }
    ) {
      ...RecordFields
      contacts {
        ...RecordContactFields
      }
      taggings {
        ...RecordTaggingFields
      }
      times(order_by: { start_at: asc }) {
        ...RecordTimeFields
      }
    }
  }
`)

// The documents a record puts up for review: its outbound links under the
// given field keys. Each target carries its `data` (the content the review
// renders) and its own `links`, which is what lets useMarkdown resolve the
// `mentions` references in that content into live record chips.
//
// Outbound links are the cheap direction — p95 of 5 per record (see
// hasura/summary.ts) — so the whole set comes back in one query.
export const fetchReviewDocuments = graphql(`
  query fetchReviewDocuments($recordId: Int!, $keys: [String!]!) {
    record_links(
      where: { record_id: { _eq: $recordId }, schema_key: { _in: $keys } }
      order_by: { id: asc }
    ) {
      id
      schema_key
      linked {
        ...RecordFields
        links {
          ...RecordLinkFields
        }
      }
    }
  }
`)

// A document's note thread: its child records, nested four levels deep.
//
// The depth is fixed because GraphQL can't recurse, and it's the same number
// DocumentNote.vue caps replies at (MAX_NOTE_DEPTH) — a reply the query can't
// reach is a reply the UI never offers to write, so nothing is silently hidden.
// A note plus how it came to be written: the provenance of its own insert, which
// is the only place an agent-authored note differs from its owner's own.
/** @public Spread by fragment name, which codegen resolves; no import needs the binding. */
export const DocumentNoteFieldsFragment = graphql(`
  fragment DocumentNoteFields on records {
    ...RecordFields
    owner_person_id
    origin: changes(
      where: { entity_type: { _eq: "record" }, operation: { _eq: "insert" } }
      limit: 1
    ) {
      provenance
    }
  }
`)

export const fetchDocumentNotes = graphql(`
  query fetchDocumentNotes($parentId: Int!) {
    records(
      where: { parent_id: { _eq: $parentId }, archived_at: { _is_null: true } }
      order_by: { created_at: asc }
    ) {
      ...DocumentNoteFields
      children(where: { archived_at: { _is_null: true } }, order_by: { created_at: asc }) {
        ...DocumentNoteFields
        children(where: { archived_at: { _is_null: true } }, order_by: { created_at: asc }) {
          ...DocumentNoteFields
          children(where: { archived_at: { _is_null: true } }, order_by: { created_at: asc }) {
            ...DocumentNoteFields
          }
        }
      }
    }
  }
`)

export const fetchAllRecords = graphql(`
  query fetchAllRecords($type: String, $orderBy: [records_order_by!] = { name: asc }) {
    records(where: { type: { _eq: $type } }, order_by: $orderBy) {
      ...RecordFields
      owner: owner_person {
        id
        name
      }
      parent {
        ...RecordFields
      }
      members_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`)

// Investment vehicles for the pipeline's fund filter. The fund family is a
// naming convention, not a schema fact — some vehicles carry the Investment
// Vehicle schema and some don't — so the caller passes the name pattern
// ("Heavybit Holdings%") and gets every live organization matching it.
export const fetchInvestmentVehicles = graphql(`
  query fetchInvestmentVehicles($namePattern: String!) {
    records(
      where: {
        type: { _eq: "organization" }
        name: { _ilike: $namePattern }
        archived_at: { _is_null: true }
      }
      order_by: { name: asc }
    ) {
      id
      name
    }
  }
`)

export const fetchPipelines = graphql(`
  query fetchPipelines($orderBy: [records_order_by!] = { created_at: desc }) {
    records(
      where: { type: { _eq: "list" }, data: { _contains: { record_type: "list" } } }
      order_by: $orderBy
    ) {
      ...RecordFields
      owner: owner_person {
        id
        name
      }
      sources {
        ...RecordSourceFields
      }
      listings {
        ...RecordListingFields
        list {
          ...RecordFields
        }
      }
      taggings {
        ...RecordTaggingFields
      }
      members_aggregate(where: { record: { type: { _eq: "list" } } }) {
        aggregate {
          count
        }
      }
    }
  }
`)

// Slim search used by task modals to populate "related/linked records" pickers.
// Returns only the fields needed to render an autocomplete row + persist a link.
export const searchRecordsForTaskLink = graphql(`
  query searchRecordsForTaskLink($where: records_bool_exp!, $limit: Int = 15) {
    records(
      where: $where
      limit: $limit
      order_by: [{ popularity: { score: desc_nulls_last } }, { name: asc }]
    ) {
      id
      type
      name
      slug
    }
  }
`)

export const fetchRecordBySource = graphql(`
  query fetchRecordBySource($sourceType: String!, $sourceId: String!) {
    source: record_sources(
      where: { source_type: { _eq: $sourceType }, source_id: { _eq: $sourceId } }
      limit: 1
    ) {
      record {
        ...RecordFields
        sources {
          ...RecordSourceFields
        }
      }
    }
  }
`)

// Batch fetch records by multiple source IDs of the same type.
// order_by(id asc): a source_id can (incorrectly) be attached to more than one
// record — see the harmonic matcher's cross-record collision guard in
// workers/harmonic/core/writeback.js. Ordering by the record_sources row's own
// id means "whoever linked first" is always first in the array, so callers that
// take sources[0] or fold into a Map keyed by source_id get a deterministic,
// stable answer instead of whatever order Postgres happens to return.
export const fetchRecordsForSources = graphql(`
  query fetchRecordsForSources($sourceType: String!, $sourceIds: [String!]!) {
    sources: record_sources(
      where: { source_type: { _eq: $sourceType }, source_id: { _in: $sourceIds } }
      order_by: { id: asc }
    ) {
      source_id
      record {
        ...RequiredRecordFields
      }
    }
  }
`)

// Like fetchRecordsForSources but also selects description + data (for avatar) so a
// hover-card can render richer detail. Kept separate from the slim, hot
// fetchRecordsForSources so its many callers aren't burdened with the extra payload.
// order_by(id asc): see fetchRecordsForSources above — same duplicate-source_id hazard.
export const fetchRecordCardsForSources = graphql(`
  query fetchRecordCardsForSources($sourceType: String!, $sourceIds: [String!]!) {
    sources: record_sources(
      where: { source_type: { _eq: $sourceType }, source_id: { _in: $sourceIds } }
      order_by: { id: asc }
    ) {
      source_id
      record {
        id
        type
        name
        slug
        description
        data
        avatar {
          url
        }
      }
    }
  }
`)
// The people the admin LinkedIn importer may import FOR, and how many
// connections each already has.
//
// Membership of the "Heavybit Employees" list is the allowlist. Looked up by
// SLUG rather than a hard-coded id so the query survives the list being
// rebuilt, and scoped to person members because the list is a record list and
// nothing guarantees its contents are all people.
//
// The connection count rides along because the import REPLACES the field: the
// number is what tells you you're about to overwrite someone's existing graph.
export const fetchLinkedinImportOwners = graphql(`
  query fetchLinkedinImportOwners($slug: String!) {
    lists: records(where: { type: { _eq: "list" }, slug: { _eq: $slug } }, limit: 1) {
      id
      name
      members(where: { record: { type: { _eq: "person" } } }) {
        record {
          id
          name
          slug
          linkedin_connections: links_aggregate(
            where: { schema_key: { _eq: "linkedin_connections" } }
          ) {
            aggregate {
              count
            }
          }
        }
      }
    }
  }
`)

// The LinkedIn connections import's match index, fetched whole rather than
// queried per CSV row. The lookup universe is the CRM's people (~22k records,
// ~13k LinkedIn contacts), not the upload — so two paginated sweeps cost the
// same whether the export has 500 rows or 50,000, where the per-row shape cost
// one to two round trips each. Both select the minimum: matching needs an id,
// and everything else is re-read later from the rows that actually matched.
//
// Keyset pagination on `id` rather than offset: the page boundary stays stable
// if rows are inserted mid-sweep, and it matches how the Harmonic queue walks
// large tables (workers/harmonic/core/queue.js).
export const fetchPersonLinkedinContactPage = graphql(`
  query fetchPersonLinkedinContactPage($afterId: Int!, $limit: Int!) {
    record_contacts(
      where: {
        _and: [
          { id: { _gt: $afterId } }
          { contact_type: { _in: ["url", "link"] } }
          { label: { _ilike: "%linkedin%" } }
          { record: { type: { _eq: "person" } } }
        ]
      }
      order_by: { id: asc }
      limit: $limit
    ) {
      id
      record_id
      value
    }
  }
`)

export const fetchPersonNamePage = graphql(`
  query fetchPersonNamePage($afterId: Int!, $limit: Int!) {
    records(
      where: { _and: [{ type: { _eq: "person" } }, { id: { _gt: $afterId } }] }
      order_by: { id: asc }
      limit: $limit
    ) {
      id
      name
    }
  }
`)

export const findRecordsByDomain = graphql(`
  query findRecordsByDomain($pattern: String!) {
    record_contacts(
      where: {
        contact_type: { _eq: "url" }
        value: { _ilike: $pattern }
        record: { type: { _eq: "organization" } }
      }
      limit: 10
    ) {
      value
      record {
        id
        type
        name
        slug
      }
    }
  }
`)

// RECORD BROWSER QUERIES ======================================================

// Every record of one type: scalars + light relations (no links/backlinks —
// useLinkedRecords attaches those). The spine query for RecordsView and the
// collection composables built on it (useTaskRecords).
export const fetchRecordsBrowser = graphql(`
  query fetchRecordsBrowser($type: String!) {
    records(where: { type: { _eq: $type } }, order_by: { name: asc }) {
      ...RecordFields
    }
  }
`)

// Tasks linked to a set of records by one link key — `related_to` for the work
// arising from a record, `assigned_to` for the work a person owns. Powers
// TasksBlock, which scopes the ids to the record it renders on (see
// `taskScopeIds`). Selects every link so a row can show the other end of the
// relationship: who a task is assigned to, or what it relates to.
export const fetchLinkedTasks = graphql(`
  query FetchLinkedTasks($ids: [Int!]!, $linkKey: String!) {
    tasks: records(
      where: {
        type: { _eq: "task" }
        archived_at: { _is_null: true }
        links: { schema_key: { _eq: $linkKey }, linked_id: { _in: $ids } }
      }
      order_by: { created_at: desc }
    ) {
      ...RecordFields
      links {
        ...RecordLinkBrowserFields
      }
    }
  }
`)

// Slim by-ids fetch — just enough to identify/render a record (id, type, name,
// slug, schema_id). Used by useLinkedRecords to resolve link endpoints without
// shipping the full record payload + nested taggings/contacts per endpoint.
export const fetchRequiredRecordsByIds = graphql(`
  query fetchRequiredRecordsByIds($ids: [Int!]!) {
    records(where: { id: { _in: $ids } }) {
      ...RequiredRecordFields
    }
  }
`)

// Batched duplicate detection query (prevents N+1 queries)
// Mutations for creating records with nested data
// Atomic mutations for creating records with listings (prevents orphaned records)
// Batch mutations for creating multiple records at once with listings (improved performance)

// Fetch records by schema ID
export const fetchRecordsBySchema = graphql(`
  query fetchRecordsBySchema($schemaId: Int!) {
    records(where: { schema_id: { _eq: $schemaId } }, order_by: { name: asc }) {
      ...RecordFields
      sources {
        ...RecordSourceFields
      }
    }
  }
`)

export const fetchExactDuplicateGroups = graphql(`
  query fetchExactDuplicateGroups($recordType: String!) {
    duplicate_exact_groups(
      where: { record_type: { _eq: $recordType } }
      order_by: { member_count: desc }
    ) {
      source_type
      source_id
      record_type
      record_ids
      member_count
    }
  }
`)

export const fetchFuzzyDuplicatePairs = graphql(`
  query fetchFuzzyDuplicatePairs($recordType: String!) {
    duplicate_fuzzy_pairs(
      where: { record_type: { _eq: $recordType } }
      order_by: { match_domain: asc }
    ) {
      record_type
      record_a_id
      record_b_id
      match_domain
      match_reason
    }
  }
`)

export const fetchNameDuplicateGroups = graphql(`
  query fetchNameDuplicateGroups($recordType: String!) {
    duplicate_name_groups(
      where: { record_type: { _eq: $recordType } }
      order_by: [{ member_count: desc }, { name_key: asc }]
    ) {
      record_type
      name_key
      record_ids
      member_count
    }
  }
`)

export const fetchHarmonicPendingConflicts = graphql(`
  query fetchHarmonicPendingConflicts($recordType: String!) {
    harmonic_pending_conflicts(
      where: { record_type: { _eq: $recordType } }
      order_by: { last_attempted_at: desc }
    ) {
      record_id
      conflict_record_id
      record_type
      enrichment_urn
      last_attempted_at
    }
  }
`)

export const fetchDuplicateGroupMembers = graphql(`
  query fetchDuplicateGroupMembers($ids: [Int!]!) {
    records(where: { id: { _in: $ids } }) {
      ...RecordFields
      contacts {
        id
        contact_type
        value
        label
        is_primary
      }
      sources {
        ...RecordSourceFields
      }
      attachments {
        id
      }
    }
  }
`)

export const fetchRecordInfillHistory = graphql(`
  query fetchRecordInfillHistory($recordId: Int!) {
    record_changes(where: { record_id: { _eq: $recordId } }, order_by: { created_at: asc }) {
      id
      created_at
      operation
      provenance
      entity_type
      entity_id
      changes
    }
  }
`)
